import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SignificantFiguresTool() {
  const [number, setNumber] = useState("");

  const countSigFigs = (num: string) => {
    if (!num || num === "") return null;
    
    // Remove whitespace
    num = num.trim();
    
    // Handle scientific notation
    if (num.includes('e') || num.includes('E')) {
      const [mantissa] = num.split(/[eE]/);
      num = mantissa;
    }
    
    // Remove leading zeros and decimal point for counting
    let cleaned = num.replace(/^-?0*\.?0*/, '');
    
    // If number has decimal point, count all remaining digits
    if (num.includes('.')) {
      cleaned = cleaned.replace('.', '');
      // Remove trailing zeros only if there's no decimal in original
      if (!num.match(/\.\d*[1-9]/)) {
        cleaned = cleaned.replace(/0+$/, '');
      }
    } else {
      // For whole numbers, trailing zeros may not be significant
      // Count all non-zero digits and zeros between them
      cleaned = cleaned.replace(/0+$/, '');
    }
    
    return cleaned.length || 1;
  };

  const roundToSigFigs = (num: string, sigFigs: number) => {
    const value = parseFloat(num);
    if (isNaN(value)) return null;
    
    if (value === 0) return "0";
    
    const magnitude = Math.floor(Math.log10(Math.abs(value)));
    const scale = Math.pow(10, magnitude - sigFigs + 1);
    const rounded = Math.round(value / scale) * scale;
    
    return rounded.toPrecision(sigFigs);
  };

  const sigFigs = countSigFigs(number);
  const examples = sigFigs ? [
    roundToSigFigs(number, Math.max(1, sigFigs - 1)),
    roundToSigFigs(number, sigFigs),
    roundToSigFigs(number, sigFigs + 1),
  ] : [];

  const getRules = () => {
    const rules = [];
    const num = number.trim();
    
    if (num.match(/^-?[1-9]/)) rules.push("Non-zero digits are always significant");
    if (num.match(/0/)) rules.push("Zeros between non-zero digits are significant");
    if (num.match(/\.\d*0+$/)) rules.push("Trailing zeros after decimal point are significant");
    if (num.match(/^-?0+\./)) rules.push("Leading zeros are NOT significant");
    if (num.match(/[1-9]0+$/)) rules.push("Trailing zeros without decimal may not be significant");
    
    return rules;
  };

  return (
    <PageLayout calculatorName="Significant Figures Tool" calculatorPath="/calculator/significant-figures">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Significant Figures Counter</CardTitle>
            <CardDescription>
              Enter a number to count its significant figures
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="number">Number</Label>
              <Input
                id="number"
                type="text"
                placeholder="e.g., 0.00456 or 1200 or 3.00"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </div>

            {sigFigs !== null && (
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-xl">Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-primary">
                    {sigFigs} significant {sigFigs === 1 ? 'figure' : 'figures'}
                  </p>
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium">Rules Applied:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      {getRules().map((rule, i) => (
                        <li key={i}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {examples.length > 0 && examples[0] !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Rounding Examples</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    <span className="font-medium text-foreground">{Math.max(1, sigFigs! - 1)} sig figs:</span> {examples[0]}
                  </p>
                  <p className="text-muted-foreground">
                    <span className="font-medium text-foreground">{sigFigs} sig figs:</span> {examples[1]}
                  </p>
                  <p className="text-muted-foreground">
                    <span className="font-medium text-foreground">{sigFigs! + 1} sig figs:</span> {examples[2]}
                  </p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Significant Figures Rules</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. All non-zero digits are significant</p>
                <p>2. Zeros between non-zero digits are significant</p>
                <p>3. Leading zeros are NOT significant</p>
                <p>4. Trailing zeros after a decimal point ARE significant</p>
                <p>5. Trailing zeros in whole numbers may not be significant</p>
                <p className="pt-2 font-medium text-foreground">
                  Examples: 0.00456 (3), 1200 (2), 3.00 (3), 100.0 (4)
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
